/**
 * 分拣过程动画 —— 场景渲染层（判断可视化）
 *
 * - 件芯片 / 托盘盒子以稳定 id 作为 DOM key（data-id / data-no），跨帧复用元素。
 * - 明细件按「(位置, 唛头前缀)」聚合为一张芯片 `前缀 n/total`：
 *     n = 该前缀在当前位置（某托盘 / 待分配区）的件数；
 *     total = 该前缀在整批明细中的总件数。
 *   同一唛头不再平铺大量重复卡片。芯片 key = `${loc}::${prefix}`（loc=托盘号 或 __unassigned__）。
 * - 不用 GSAP FLIP 做件芯片的位置过渡：FLIP 会把「计数宽度变化/flex 重排」当成位移逐帧动画，
 *   导致已分配/待分配芯片整体飘动。改为：芯片宽度固定 + 仅新建芯片 CSS 淡入，其余原位不动。
 * - active：本步变动元素加 .active；focus：聚焦对象加 .anim-focus（更强高亮）。
 * - 托盘双进度条（重量/体积，瓶颈高亮）+ status 样式（open/sealed/removed）。
 * - 条件判定流程图（decision）；合并候选对分轨评估区（focus.pair）。
 */

import type { DecisionKind, Frame, Item, Pallet } from './types';

/** 待分配区位置标识（聚合芯片 key 用） */
const UNASSIGNED = '__unassigned__';

/** 条件闸门状态：通过=绿，失败=红，未到达=灰 */
type GateState = 'pass' | 'fail' | 'idle';
interface Gate {
  label: string;
  detail?: string;
  state: GateState;
}
interface DecisionFlow {
  gates: Gate[];
  outcome: Gate;
}

function pct(n: number | undefined): string {
  return n == null ? '' : `${Math.round(n * 100)}%`;
}

/** 把 decision 映射成「条件闸门 → 结论」的流程图模型；无判断（或非条件判断）时返回 null */
function decisionFlow(d: { kind: DecisionKind; values?: Record<string, number> } | undefined): DecisionFlow | null {
  if (!d) return null;
  const v = d.values ?? {};
  switch (d.kind) {
    case 'capacity-ok':
      return { gates: [{ label: '容量', detail: `体${v.newVolume}≤${v.volumeLimit}`, state: 'pass' }], outcome: { label: '✓ 装入', state: 'pass' } };
    case 'capacity-overflow':
      return { gates: [{ label: '容量', detail: `体${v.newVolume}/${v.volumeLimit}`, state: 'fail' }], outcome: { label: '✗ 封箱', state: 'fail' } };
    case 'oversized':
      return { gates: [{ label: '单件', detail: `体${v.volume}/${v.volumeLimit}`, state: 'fail' }], outcome: { label: '✗ 超限独占', state: 'fail' } };
    case 'merge-ok':
      return {
        gates: [
          { label: '单位', state: 'pass' },
          { label: '容量', detail: `重${pct(v.weightUtil)} 体${pct(v.volumeUtil)}`, state: 'pass' },
          { label: '唛头', detail: '≤2', state: 'pass' },
        ],
        outcome: { label: `✓ 可合并${v.tightness != null ? ' ' + v.tightness : ''}`, state: 'pass' },
      };
    case 'mark-blocked':
      return {
        gates: [
          { label: '单位', state: 'pass' },
          { label: '容量', state: 'pass' },
          { label: '唛头', detail: `${v.distinct}>${v.limit}`, state: 'fail' },
        ],
        outcome: { label: '✗ 唛头阻断', state: 'fail' },
      };
    case 'limit-overflow':
      return {
        gates: [
          { label: '单位', state: 'pass' },
          { label: '容量', state: 'fail' },
          { label: '唛头', state: 'idle' },
        ],
        outcome: { label: '✗ 容量阻断', state: 'fail' },
      };
    case 'unit-mismatch':
      return {
        gates: [
          { label: '单位', state: 'fail' },
          { label: '容量', state: 'idle' },
          { label: '唛头', state: 'idle' },
        ],
        outcome: { label: '✗ 单位阻断', state: 'fail' },
      };
    default:
      return null; // tightness-rank 等非条件判断，不画流程
  }
}

export interface RenderOptions {
  /** true=FLIP 过渡；false=瞬时 snap（seek 打断） */
  flip?: boolean;
}

interface DesiredChip {
  /** 芯片稳定 key（DOM data-id） */
  id: string;
  /** 所属位置：托盘号 或 UNASSIGNED */
  loc: string;
  /** 唛头前缀 */
  prefix: string;
  /** 计数文本，如 `40/100` 或 `×2` */
  countText: string;
}

export class SceneRenderer {
  private root: HTMLElement;
  private captionEl: HTMLElement;
  private decisionEl: HTMLElement;
  private metricsEl: HTMLElement;
  private pairPanelEl: HTMLElement;
  private unassignedEl: HTMLElement;
  private palletsEl: HTMLElement;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.innerHTML = `
      <div class="anim-header">
        <div class="anim-caption"></div>
        <div class="anim-metrics"></div>
      </div>
      <div class="anim-info-band">
        <div class="anim-pair-panel" data-el="pairPanel" hidden></div>
        <div class="anim-decision" data-el="decision" hidden></div>
      </div>
      <div class="anim-stage">
        <div class="anim-unassigned-area">
          <div class="anim-area-label">待分配</div>
        </div>
        <div class="anim-pallets-area"></div>
      </div>
    `;
    this.captionEl = this.root.querySelector('.anim-caption') as HTMLElement;
    this.decisionEl = this.root.querySelector('[data-el="decision"]') as HTMLElement;
    this.metricsEl = this.root.querySelector('.anim-metrics') as HTMLElement;
    this.pairPanelEl = this.root.querySelector('[data-el="pairPanel"]') as HTMLElement;
    this.unassignedEl = this.root.querySelector('.anim-unassigned-area') as HTMLElement;
    this.palletsEl = this.root.querySelector('.anim-pallets-area') as HTMLElement;
  }

  /** 渲染一帧（件芯片不做位置过渡——杜绝逐帧飘动；仅新建芯片 CSS 淡入） */
  render(frame: Frame, _opts: RenderOptions = {}): void {
    this.reconcile(frame);
    this.applyActiveFocus(frame);
    this.applyCaptionMetricsDecision(frame);
  }

  /** 清空场景 */
  clear(): void {
    this.unassignedEl.querySelectorAll('.anim-item').forEach(e => e.remove());
    this.palletsEl.querySelectorAll('.anim-pallet').forEach(e => e.remove());
  }

  private palletBoxes(): Map<string, HTMLElement> {
    const map = new Map<string, HTMLElement>;
    this.palletsEl.querySelectorAll<HTMLElement>('.anim-pallet').forEach(el => {
      map.set(el.dataset.no!, el);
    });
    return map;
  }

  private itemChips(): Map<string, HTMLElement> {
    const map = new Map<string, HTMLElement>();
    this.root.querySelectorAll<HTMLElement>('.anim-item').forEach(el => {
      map.set(el.dataset.id!, el);
    });
    return map;
  }

  /** 将 DOM 调整为与帧一致（托盘增删/更新 + 明细芯片按前缀聚合） */
  private reconcile(frame: Frame): void {
    // --- 托盘盒子 ---
    const desiredPallets = new Map(frame.pallets.map(p => [p.palletNo, p]));
    this.palletBoxes().forEach((el, no) => {
      if (!desiredPallets.has(no)) el.remove();
    });
    frame.pallets.forEach(p => {
      let box = this.palletBoxes().get(p.palletNo);
      if (!box) {
        box = this.createPalletBox();
        this.palletsEl.appendChild(box); // 仅新建托盘追加到末尾；已存在托盘保持原位，不每帧重排
      }
      this.updatePalletBox(box, p);
    });

    // --- 明细芯片（按 前缀 聚合） ---
    const desired = this.buildDesiredChips(frame);
    this.itemChips().forEach((el, id) => {
      if (!desired.has(id)) el.remove();
    });
    desired.forEach(info => {
      let chip = this.itemChips().get(info.id);
      if (!chip) {
        chip = this.createAggregateChip(info.id);
        chip.classList.add('anim-item-in'); // 新建芯片淡入进场（唯一允许的"飘"）
        this.parentForLoc(info.loc).appendChild(chip); // 仅新建芯片追加；已存在芯片保持原位，不每帧重排
      }
      this.updateAggregateChip(chip, info);
    });
  }

  /** 由帧构建期望的聚合芯片集合 */
  private buildDesiredChips(frame: Frame): Map<string, DesiredChip> {
    const desired = new Map<string, DesiredChip>();
    // total = 每个前缀在整批【明细件】中的总件数（折叠唛头芯片不计入）
    const totalByPrefix = new Map<string, number>();
    frame.items.forEach(it => {
      if (it.markSerialNo) totalByPrefix.set(it.markPrefix, (totalByPrefix.get(it.markPrefix) ?? 0) + 1);
    });

    // 按位置 + 前缀聚合明细件
    const bucket = new Map<string, Map<string, number>>();
    const ensureBucket = (loc: string): Map<string, number> => {
      let m = bucket.get(loc);
      if (!m) { m = new Map(); bucket.set(loc, m); }
      return m;
    };

    frame.items.forEach(it => {
      if (!it.markSerialNo) {
        // 折叠唛头芯片（raw 帧）：逐个渲染，文本 `前缀 ×quantity`
        desired.set(it.id, { id: it.id, loc: UNASSIGNED, prefix: it.markPrefix, countText: `×${it.quantity ?? 1}` });
        return;
      }
      const loc = it.palletNo ?? UNASSIGNED;
      const m = ensureBucket(loc);
      m.set(it.markPrefix, (m.get(it.markPrefix) ?? 0) + 1);
    });

    bucket.forEach((prefixMap, loc) => {
      prefixMap.forEach((n, prefix) => {
        const total = totalByPrefix.get(prefix) ?? n;
        desired.set(`${loc}::${prefix}`, { id: `${loc}::${prefix}`, loc, prefix, countText: `${n}/${total}` });
      });
    });
    return desired;
  }

  /** 芯片应归属的父容器：UNASSIGNED → 待分配区；否则对应托盘盒内件区 */
  private parentForLoc(loc: string): HTMLElement {
    if (loc === UNASSIGNED) return this.unassignedEl;
    const box = this.palletBoxes().get(loc);
    if (box) return box.querySelector('.anim-pallet-items') as HTMLElement;
    return this.unassignedEl;
  }

  /** 件 → 其聚合芯片 key（用于 active/focus 高亮映射） */
  private chipIdForItem(it: Item): string {
    if (!it.markSerialNo) return it.id; // 折叠唛头芯片
    return `${it.palletNo ?? UNASSIGNED}::${it.markPrefix}`;
  }

  private createPalletBox(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'anim-pallet';
    el.innerHTML = `
      <div class="anim-pallet-head">
        <span class="anim-pallet-no"></span>
        <span class="anim-pallet-status"></span>
      </div>
      <div class="anim-pallet-cust"></div>
      <div class="anim-pallet-bars">
        <div class="anim-bar" data-bar="weight">
          <span class="anim-bar-label">重</span>
          <div class="anim-bar-track"><div class="anim-bar-fill"></div></div>
          <span class="anim-bar-val"></span>
        </div>
        <div class="anim-bar" data-bar="volume">
          <span class="anim-bar-label">体</span>
          <div class="anim-bar-track"><div class="anim-bar-fill"></div></div>
          <span class="anim-bar-val"></span>
        </div>
      </div>
      <div class="anim-pallet-stats"></div>
      <div class="anim-pallet-items"></div>
    `;
    return el;
  }

  private updatePalletBox(el: HTMLElement, p: Pallet): void {
    el.dataset.no = p.palletNo;
    el.classList.toggle('anim-pallet-open', p.status === 'open');
    el.classList.toggle('anim-pallet-sealed', p.status === 'sealed');
    el.classList.toggle('anim-pallet-removed', p.status === 'removed');
    (el.querySelector('.anim-pallet-no') as HTMLElement).textContent = p.palletNo;
    (el.querySelector('.anim-pallet-status') as HTMLElement).textContent =
      p.status === 'sealed' ? '封箱' : p.status === 'removed' ? '已移除' : '';
    (el.querySelector('.anim-pallet-cust') as HTMLElement).textContent = p.sku || '—';

    // 双进度条（重量/体积），瓶颈高亮
    const bars = el.querySelectorAll<HTMLElement>('.anim-bar');
    const setBar = (bar: HTMLElement, util: number, isBottleneck: boolean): void => {
      bar.classList.toggle('anim-bar-bottleneck', isBottleneck);
      const fill = bar.querySelector('.anim-bar-fill') as HTMLElement;
      fill.style.width = `${Math.min(100, Math.max(0, util * 100))}%`;
      (bar.querySelector('.anim-bar-val') as HTMLElement).textContent = `${Math.round(util * 100)}%`;
    };
    setBar(bars[0], p.utilization.weight, p.utilization.bottleneck >= p.utilization.volume - 1e-9);
    setBar(bars[1], p.utilization.volume, p.utilization.volume >= p.utilization.weight - 1e-9);

    (el.querySelector('.anim-pallet-stats') as HTMLElement).textContent =
      `${p.itemCount}件 · ${p.totalVolume.toFixed(2)}CBM · ${p.totalWeight.toFixed(2)}KGS`;
    // 注：distinct 唛头不再单独展示——件区内的「前缀 n/total」芯片已表达唛头归属
  }

  private createAggregateChip(id: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'anim-item';
    el.dataset.id = id;
    el.innerHTML = `<span class="anim-item-label"></span><span class="anim-item-count"></span>`;
    return el;
  }

  private updateAggregateChip(el: HTMLElement, info: DesiredChip): void {
    el.dataset.id = info.id;
    (el.querySelector('.anim-item-label') as HTMLElement).textContent = info.prefix;
    (el.querySelector('.anim-item-count') as HTMLElement).textContent = info.countText;
    el.title = `${info.prefix} ${info.countText}`;
  }

  /** 高亮本步变动元素（active）与聚焦对象（focus），并标注 mark-blocked */
  private applyActiveFocus(frame: Frame): void {
    this.root.querySelectorAll(
      '.anim-item.active, .anim-pallet.active, .anim-item.anim-focus, .anim-pallet.anim-focus, .anim-pallet.anim-mark-blocked'
    ).forEach(el => {
      el.classList.remove('active', 'anim-focus', 'anim-mark-blocked');
    });

    // 件 id → 聚合芯片 key（同一前缀的件映射到同一芯片）
    const chipIdByItem = new Map<string, string>();
    frame.items.forEach(it => chipIdByItem.set(it.id, this.chipIdForItem(it)));

    const active = frame.active;
    if (active) {
      active.items?.forEach(id => {
        const chipId = chipIdByItem.get(id);
        if (chipId) this.root.querySelector(`.anim-item[data-id="${cssEscape(chipId)}"]`)?.classList.add('active');
      });
      active.pallets?.forEach(no => {
        this.root.querySelector(`.anim-pallet[data-no="${cssEscape(no)}"]`)?.classList.add('active');
      });
    }

    const focus = frame.focus;
    if (!focus) return;
    if (focus.type === 'item' && focus.itemId) {
      const chipId = chipIdByItem.get(focus.itemId);
      if (chipId) this.root.querySelector(`.anim-item[data-id="${cssEscape(chipId)}"]`)?.classList.add('anim-focus');
    } else if (focus.type === 'pallet' && focus.palletNo) {
      this.root.querySelector(`.anim-pallet[data-no="${cssEscape(focus.palletNo)}"]`)?.classList.add('anim-focus');
    } else if (focus.type === 'pair' && focus.pair) {
      const markBlocked = frame.decision?.kind === 'mark-blocked';
      [focus.pair.target, focus.pair.source].forEach(no => {
        const pel = this.root.querySelector(`.anim-pallet[data-no="${cssEscape(no)}"]`);
        pel?.classList.add('anim-focus');
        if (markBlocked) pel?.classList.add('anim-mark-blocked');
      });
    }
  }

  /** 渲染条件判定流程图（通路绿/失败红/未达灰），无判断时隐藏 */
  private renderDecisionFlow(decision: Frame['decision']): void {
    const flow = decisionFlow(decision);
    if (!flow) {
      this.decisionEl.hidden = true;
      return;
    }
    this.decisionEl.hidden = false;
    this.decisionEl.className = 'anim-decision anim-flow';
    let html = '';
    flow.gates.forEach((g, i) => {
      if (i > 0) html += `<span class="anim-flow-arrow ${g.state}">→</span>`;
      html += `<span class="anim-flow-node ${g.state}">${g.label}${g.detail ? `<span class="anim-flow-detail">${g.detail}</span>` : ''}</span>`;
    });
    html += `<span class="anim-flow-arrow ${flow.outcome.state}">→</span>`;
    html += `<span class="anim-flow-outcome ${flow.outcome.state}">${flow.outcome.label}</span>`;
    this.decisionEl.innerHTML = html;
  }

  private applyCaptionMetricsDecision(frame: Frame): void {
    this.captionEl.textContent = frame.caption;

    // 条件判定流程图（通路绿/否则红）
    this.renderDecisionFlow(frame.decision);

    // 指标
    const m = frame.metrics;
    const parts: string[] = [`当前托盘数：${m.palletCount}`];
    if (m.originalPalletCount != null && m.mergedPalletCount != null) {
      parts.push(`合并前 ${m.originalPalletCount} → 合并后 ${m.mergedPalletCount}`);
      if (m.savingRatio != null) parts.push(`节省 ${m.savingRatio}%`);
    }
    this.metricsEl.textContent = parts.join('　·　');

    // 合并候选对分轨评估区
    const focus = frame.focus;
    if (focus?.type === 'pair' && focus.pair) {
      this.pairPanelEl.hidden = false;
      const pr = focus.pair;
      this.pairPanelEl.innerHTML = `
        <span class="anim-pair-target">${pr.target}</span>
        <span class="anim-pair-arrow">← 合并 ←</span>
        <span class="anim-pair-source">${pr.source}</span>
        <span class="anim-pair-tightness">紧度 ${pr.tightness.toFixed(2)}</span>
      `;
    } else {
      this.pairPanelEl.hidden = true;
    }
  }
}

/** CSS 选择器转义（id/no 可能含特殊字符） */
function cssEscape(value: string): string {
  return (window.CSS && CSS.escape) ? CSS.escape(value) : value.replace(/["\\]/g, '\\$&');
}
